import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Input,
  FormField,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { PortalShell } from "../components/PortalShell";
import { brokerApi } from "../api/broker";
import { PortalApiError } from "../api/client";
import type { BrokerApplicationResubmitDto } from "../api/types";

// FixBatch-1 #11 — Token-protected resubmit page. The applicant lands
// here from the return-with-comments email and edits the form their
// reviewer saw. On submit the application transitions back to
// Submitted and re-enters the staff approval queue.

interface FormState {
  companyName: string;
  contactName: string;
  phone: string;
  website: string;
  state: string;
}

export function BrokerResubmitPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<BrokerApplicationResubmitDto | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setError("Missing resubmit token.");
        setLoading(false);
        return;
      }
      try {
        const dto = await brokerApi.getResubmitApplication(token);
        if (cancelled) return;
        setApplication(dto);
        setForm({
          companyName: dto.companyName,
          contactName: dto.contactName,
          phone: dto.phone ?? "",
          website: dto.website ?? "",
          state: dto.state ?? "",
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof PortalApiError ? err.message
          : err instanceof Error ? err.message
            : "We couldn't load this application.";
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [token]);

  const update = (k: keyof FormState) => (v: string) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const resubmit = useSubmittingMutation(
    async () => {
      if (!token || !form) return;
      await brokerApi.resubmitApplication(token, {
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        phone: form.phone.trim() || undefined,
        website: form.website.trim() || undefined,
        state: form.state.trim() || undefined,
      });
      setSubmitted(true);
    },
    {
      onError: (err) => {
        const msg = err instanceof PortalApiError ? err.message
          : err instanceof Error ? err.message
            : "Could not resubmit. Please try again.";
        setError(msg);
      },
    },
  );
  const submitting = resubmit.pending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token || !form) return;
    if (!form.companyName.trim() || !form.contactName.trim()) {
      setError("Company name and contact name are required.");
      return;
    }
    void resubmit.submit();
  }

  if (loading) {
    return (
      <PortalShell pageTitle="Resubmit application">
        <p className="text-small text-gray-mid">Loading…</p>
      </PortalShell>
    );
  }

  if (!application || !form) {
    return (
      <PortalShell pageTitle="Resubmit application">
        <h1 className="text-h2 font-display text-onyx m-0">Link not available</h1>
        <p className="text-small text-onyx mt-space-3">
          {error ?? "This resubmit link is no longer active. If you've already resubmitted, our team will follow up by email."}
        </p>
        <Button className="mt-space-4" variant="secondary" onClick={() => navigate("/portal/apply")}>
          Start a new application
        </Button>
      </PortalShell>
    );
  }

  if (submitted) {
    return (
      <PortalShell pageTitle="Application resubmitted">
        <h1 className="text-h2 font-display text-onyx m-0">Thanks — your updates are in</h1>
        <p className="text-small text-onyx mt-space-3">
          Our broker team has the latest version of your application and will be in touch by email.
        </p>
      </PortalShell>
    );
  }

  return (
    <PortalShell pageTitle="Resubmit application">
      <h1 className="text-h2 font-display text-onyx m-0">Update your broker application</h1>
      <p className="text-small text-gray-mid mt-space-2">
        Our reviewer left a note below. Update the fields and resubmit.
      </p>

      {application.reviewerNote && (
        <div
          className="mt-space-4 p-space-3 border border-status-warning rounded-md bg-white-pure"
          data-testid="broker-resubmit-note"
        >
          <p className="text-caption text-gray-mid m-0">Reviewer note</p>
          <p className="text-small text-onyx m-0 mt-space-1">{application.reviewerNote}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-space-5 flex flex-col gap-space-4" noValidate>
        <FormField label="Company name" required>
          <Input
            value={form.companyName}
            onChange={(e) => update("companyName")(e.target.value)}
            disabled={submitting}
            data-testid="broker-resubmit-company"
          />
        </FormField>
        <FormField label="Contact name" required>
          <Input
            value={form.contactName}
            onChange={(e) => update("contactName")(e.target.value)}
            disabled={submitting}
            data-testid="broker-resubmit-name"
          />
        </FormField>
        <FormField label="Contact email">
          <Input value={application.contactEmail} disabled readOnly />
        </FormField>
        <FormField label="Phone">
          <Input
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone")(e.target.value)}
            disabled={submitting}
          />
        </FormField>
        <FormField label="Website">
          <Input
            value={form.website}
            onChange={(e) => update("website")(e.target.value)}
            disabled={submitting}
          />
        </FormField>
        <FormField label="State">
          <Input
            value={form.state}
            onChange={(e) => update("state")(e.target.value)}
            disabled={submitting}
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error m-0" role="alert">{error}</p>
        )}

        <SubmitButton
          type="submit"
          pending={submitting}
          pendingLabel="Resubmitting…"
          data-testid="broker-resubmit-submit"
        >
          Resubmit application
        </SubmitButton>
        <SubmittingHint pending={submitting} />
      </form>
    </PortalShell>
  );
}
